import React from 'react'
import { type Control } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { Typography } from '~/components/elements/Typography'
import {
  BrandGroupSelector,
  type BrandGroupSelectorProps,
} from '~/components/features/BrandGroupSelector'
import {
  BrandSelector,
  type BrandSelectorProps,
} from '~/components/features/BrandSelector'
import {
  NineSegmentsCheckPanel,
  type NineSegmentsCheckPanelProps,
} from '~/components/features/NineSegmentsCheckPanel'
import { SelectorListContainer } from '~/components/features/SelectorListContainer'
import { useFormField } from '~/hooks/useFormField'
import {
  ensureExists,
  findByPath,
  type NineNumber,
  pathAssume,
  toNineNumber,
  toNineSegment,
} from '~/utils'

import { type Brand, type BrandGroup, type InputValues } from '../utils'

import styles from './BrandSegmentInput.css'

export type BrandSegmentInputProps = {
  brandGroups: BrandGroup[]
  control: Control<InputValues>
  filterGroupIndex: number
  filterIndex: number
}

export const BrandSegmentInput = ({
  brandGroups,
  control,
  filterGroupIndex,
  filterIndex,
}: BrandSegmentInputProps) => {
  const { t } = useTranslation()
  const namePrefix =
    `filterGroups.${filterGroupIndex}.${filterIndex}.brandSegments` as const
  const brandGroupIdField = useFormField(control, `${namePrefix}.brandGroupId`)
  const brandNumberField = useFormField(control, `${namePrefix}.brandNumber`)
  const segmentNumbersField = useFormField(
    control,
    `${namePrefix}.segmentNumbers`,
  )

  const handleBrandGroupChange = React.useCallback<
    EH<BrandGroupSelectorProps<BrandGroup>, 'onChange'>
  >(
    (brandGroupId) => {
      brandGroupIdField.onChange(brandGroupId)

      const brandGroup = findByPath(brandGroups, 'id', brandGroupId)
      const brandNumber = pathAssume(brandGroup, 'brands.0.number')
      brandNumberField.onChange(brandNumber)
    },
    [brandGroupIdField, brandGroups, brandNumberField],
  )

  const brandGroup = React.useMemo(() => {
    const brandGroup = findByPath(brandGroups, 'id', brandGroupIdField.value)

    return ensureExists(brandGroup, t('assertion_failed'))
  }, [brandGroupIdField.value, brandGroups, t])

  const brandSelectorValue = React.useMemo(() => {
    const brand = findByPath(
      brandGroup.brands,
      'number',
      brandNumberField.value,
    )

    return pathAssume(brand, 'id')
  }, [brandGroup.brands, brandNumberField.value])

  const handleBrandChange = React.useCallback<
    EH<BrandSelectorProps<Brand>, 'onChange'>
  >(
    (brandId) => {
      const brand = findByPath(brandGroup.brands, 'id', brandId)
      const brandNumber = pathAssume(brand, 'number')
      brandNumberField.onChange(brandNumber)
    },
    [brandGroup.brands, brandNumberField],
  )

  const nineSegmentsCheckPanelValue = React.useMemo(
    () => (segmentNumbersField.value as NineNumber[]).map(toNineSegment),
    [segmentNumbersField.value],
  )
  const handleNineSegmentsChange = React.useCallback<
    EH<NineSegmentsCheckPanelProps, 'onChange'>
  >(
    (nineSegments) => {
      const segmentNumbers = nineSegments.map(toNineNumber)
      segmentNumbersField.onChange(segmentNumbers)
    },
    [segmentNumbersField],
  )

  return (
    <SelectorListContainer>
      <Typography fontWeight={700} variant="heading3">
        {t('brand_group_6')}
      </Typography>

      <BrandGroupSelector
        brandGroups={brandGroups}
        {...brandGroupIdField}
        onChange={handleBrandGroupChange}
      />

      <Typography fontWeight={700} variant="heading3">
        {t('brand_name_9')}
      </Typography>

      <BrandSelector
        brands={brandGroup.brands}
        {...brandNumberField}
        value={brandSelectorValue}
        onChange={handleBrandChange}
      />

      <NineSegmentsCheckPanel
        className={styles.segmentNumbersCheckPanel}
        value={nineSegmentsCheckPanelValue}
        onChange={handleNineSegmentsChange}
      />
    </SelectorListContainer>
  )
}
